import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, shareReplay, throwError } from 'rxjs';

import { CompanyUser, UserRole, User, UserThumbnailInfo } from '@shared/models';

import { BaseService } from '../base.service';
import { httpParams } from '../http-params.builder';
import { LoadersService } from '../loaders.service';

@Injectable({
  providedIn: 'root',
})
export class UsersService extends BaseService {
  private readonly userThumbnailInfoCache = new Map<string, Observable<UserThumbnailInfo>>();

  constructor(
    private http: HttpClient,
    private loadersService: LoadersService
  ) {
    super();
  }

  get(loader: string, companyId?: string, role?: UserRole, showInactive = false, ids?: string[]) {
    this.loadersService.show(loader);
    return this.http
      .get<Array<User>>(`${this.baseUrl}/users`, {
        params: httpParams().from({ role, includeInactive: showInactive, companyId }).array('ids', ids).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getById(loader: string, userId: string): Observable<User> {
    this.loadersService.show(loader);
    return this.http
      .get<User>(`${this.baseUrl}/users/${userId}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getUserThumbnailInfo(userId: string): Observable<UserThumbnailInfo> {
    const cached = this.userThumbnailInfoCache.get(userId);
    if (cached) {
      return cached;
    }

    const request = this.http.get<UserThumbnailInfo>(`${this.baseUrl}/users/${userId}/thumbnail-info`).pipe(
      catchError((error) => {
        this.userThumbnailInfoCache.delete(userId);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.userThumbnailInfoCache.set(userId, request);
    return request;
  }

  getUserCompanies(loader: string, userId: string): Observable<CompanyUser[]> {
    this.loadersService.show(loader);
    return this.http
      .get<CompanyUser[]>(`${this.baseUrl}/users/${userId}/companies`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  delete(loader: string, id: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.delete(`${this.baseUrl}/users/${id}`, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  deleteMy(loader: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.delete(`${this.baseUrl}/users/my`, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  add(loader: string, dto: User): Observable<User> {
    this.loadersService.show(loader);
    return this.http.post<User>(this.baseUrl + '/users', dto).pipe(finalize(() => this.loadersService.hide(loader)));
  }

  edit(loader: string, dto: User): Observable<User> {
    this.loadersService.show(loader);
    return this.http.put<User>(this.baseUrl + '/users', dto).pipe(finalize(() => this.loadersService.hide(loader)));
  }

  restore(loader: string, userId: string): Observable<void> {
    this.loadersService.show(loader);
    return this.http
      .put<void>(this.baseUrl + '/users/restore', { userId: userId })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  resendInvitation(loader: string, userId: string): Observable<void> {
    this.loadersService.show(loader);
    return this.http
      .post<void>(this.baseUrl + '/users/resend-invitation', { userId: userId })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  acceptTerms(loader: string): Observable<void> {
    this.loadersService.show(loader);
    return this.http
      .post<void>(this.baseUrl + '/users/accept-terms', null)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }
}
